import {
  Home,
  ListChecks,
  Users,
  Settings,
  Network,
  User,
  FileText,
  ClipboardList,
  BookTemplate,
  LogOut,
  BookOpen,
  ShieldAlert,
  LifeBuoy,
  LayoutDashboard,
  Cog,
  ClipboardCheck,
  Users2,
  Heart,
  HelpCircle,
  HeartHandshake,
  MessageCircle,
  UserSearch,
  Inbox,
  Cake,
  Briefcase,
  ShieldAlert as ShieldAlertIcon,
} from "lucide-react";
import { clearAppBadge } from "@/lib/appBadge";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link, useLocation } from "wouter";
import recoverMeLogo from "@assets/forgerestore-logo_1779932627674.png";
import { motion, AnimatePresence } from "framer-motion";
import { useAppearance } from "@/lib/appearance";
import { useQuery } from "@tanstack/react-query";
import type { MouseEvent, ReactNode } from "react";

interface AppSidebarProps {
  userRole: "sponsor" | "sponsee";
  userName: string;
  userAvatar?: string;
}

/**
 * Animated active-indicator that travels between active sidebar items using
 * framer-motion's `layoutId`. Reduce-motion users see only the static bar
 * without the cross-position spring animation.
 */
function ActivePill({ active }: { active: boolean }) {
  const { isReducedMotion } = useAppearance();
  return (
    <AnimatePresence>
      {active && (
        <motion.span
          aria-hidden
          layoutId={isReducedMotion ? undefined : "sidebar-active-pill"}
          className="absolute inset-y-1 left-0 w-1 rounded-r-md bg-primary pointer-events-none"
          initial={isReducedMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={isReducedMotion ? { opacity: 1 } : { opacity: 0 }}
          transition={{ type: "spring", stiffness: 500, damping: 38 }}
        />
      )}
    </AnimatePresence>
  );
}

/**
 * Task #177 — Admin sidebar entry. Renders only when /api/admin/me reports
 * the current user is an admin (any tier). Polls infrequently; the query is
 * shared with the Admin Portal page so navigating between them is instant.
 */
export function AdminSidebarEntry({ location }: { location: string }) {
  // /api/admin/me 404s for non-admins (Task #177 disclosure-safe gating).
  // We silence retries and treat any error/empty as "not an admin", so the
  // sidebar entry simply doesn't render for non-admins.
  const { data } = useQuery<{ isAdmin: boolean; tier: "superadmin" | "admin" }>({
    queryKey: ["/api/admin/me"],
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  if (!data?.isAdmin) return null;
  return (
    <SidebarMenuItem>
      <NavRow active={location === "/admin"}>
        <SidebarMenuButton
          asChild
          isActive={location === "/admin"}
          data-testid="link-admin-portal"
        >
          <Link href="/admin">
            <ShieldAlertIcon className="w-4 h-4" />
            <span>Admin</span>
          </Link>
        </SidebarMenuButton>
      </NavRow>
    </SidebarMenuItem>
  );
}

function NavRow({
  active,
  children,
}: {
  active: boolean;
  children: ReactNode;
}) {
  return (
    <div className="relative group/navrow [&_svg]:transition-transform [&_svg]:duration-200 hover:[&_svg]:translate-x-1">
      <ActivePill active={active} />
      {children}
    </div>
  );
}

const sharedNavItems = [
  { title: "Dashboard", url: "/", icon: Home },
  { title: "Sponsorship Family", url: "/family-tree", icon: Network },
  { title: "Journal", url: "/journal", icon: BookOpen },
  { title: "Triggers & Cravings", url: "/triggers-cravings", icon: ShieldAlert },
  { title: "Recovery Playbook", url: "/playbook", icon: LifeBuoy },
  { title: "Anniversaries", url: "/anniversaries", icon: Cake },
  { title: "Service Positions", url: "/service", icon: Briefcase },
  { title: "Assigned Tasks", url: "/assignments", icon: ClipboardCheck },
  { title: "12 Steps", url: "/steps", icon: ListChecks },
];

const sponsorPortalItems = [
  { title: "My Sponsees", url: "/sponsees", icon: Users },
  { title: "Sponsor Inquiries", url: "/sponsor-inquiries", icon: Inbox },
  { title: "Assign Tasks to Sponsee", url: "/assign-tasks", icon: ClipboardList },
  { title: "Task Templates", url: "/standard-tasks", icon: BookTemplate },
  { title: "Sponsor Library", url: "/library", icon: FileText },
  { title: "Sponsor Settings and Admin", url: "/sponsor-settings", icon: Cog },
];

const sponsorPortalPrefixes = [
  "/sponsor-portal",
  "/sponsees",
  "/sponsor-inquiries",
  "/assign-tasks",
  "/standard-tasks",
  "/library",
  "/sponsor-settings",
  "/review",
];

// Task #323 — logging out must clear the PWA icon badge so the previous
// user's unread count doesn't linger on the home-screen icon (privacy on
// shared devices). Fire-and-forget: never blocks the /api/logout navigation.
export function LogoutSidebarEntry() {
  return (
    <SidebarMenuItem>
      <NavRow active={false}>
        <SidebarMenuButton asChild data-testid="button-logout">
          <a
            href="/api/logout"
            onClick={() => {
              void clearAppBadge();
            }}
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </a>
        </SidebarMenuButton>
      </NavRow>
    </SidebarMenuItem>
  );
}

export default function AppSidebar({ userRole, userName, userAvatar }: AppSidebarProps) {
  const [location] = useLocation();
  const { isReducedMotion } = useAppearance();
  const { isMobile, setOpenMobile } = useSidebar();

  // On phones the sidebar is an overlay (a Sheet). Tapping any destination
  // should dismiss it automatically so the user never has to tap the dimmed
  // area to close it. We close on a single delegated handler (rather than
  // wiring every link) so it also covers same-page taps and any links added
  // later. Desktop keeps its persistent sidebar untouched.
  const handleNavClick = (e: MouseEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    if ((e.target as HTMLElement).closest("a")) {
      setOpenMobile(false);
    }
  };

  // Detect whether the (sponsee) user already has an active sponsor.
  // The endpoint returns the sponsor relationship row (which carries a
  // sponsorId field) or null. We only need to know if a row exists.
  type SponsorRel = { id: string; sponsorId: string; sponsor?: { id: string } } | null;
  const { data: sponsorRel, isFetched: sponsorRelFetched } = useQuery<SponsorRel>({
    queryKey: ["/api/sponsee/sponsor"],
    enabled: userRole === "sponsee",
  });
  const hasSponsor = Boolean(sponsorRel?.sponsorId);
  // Wait for the query to settle before showing the CTA so users who already
  // have a sponsor don't see a brief "Find a Sponsor" flash on first paint.
  const showFindASponsor = userRole === "sponsee" && sponsorRelFetched && !hasSponsor;

  const getInitials = () => {
    const names = userName.split(" ");
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return userName.substring(0, 2).toUpperCase();
  };

  const isSponsorPortalActive = sponsorPortalPrefixes.some(
    (prefix) => location === prefix || location.startsWith(prefix + "/")
  );

  const isCommunityPortalActive =
    location === "/community-portal" ||
    location.startsWith("/community-portal/") ||
    location === "/gratitude-lists" ||
    location === "/messages" ||
    location === "/inner-circle";
  const isGratitudeListsActive = location === "/gratitude-lists";

  return (
    <Sidebar data-testid="sidebar-nav">
      <SidebarHeader className="p-4 border-b space-y-3">
        <motion.div
          initial={isReducedMotion ? false : { opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-2"
        >
          {/* Task #189 — sidebar brand lockup is locked to fixed ForgeRestore
              palette so the wordmark renders the same regardless of which app
              theme the user has selected. */}
          <img src={recoverMeLogo} alt="ForgeRestore" className="w-8 h-8 object-contain" />
          <h2 className="text-lg font-semibold text-[#0A4D68] dark:text-[#FFB84C]">ForgeRestore</h2>
        </motion.div>

        <div className="flex items-center gap-3">
          <Avatar className="w-10 h-10">
            <AvatarImage src={userAvatar} alt={userName} />
            <AvatarFallback>{getInitials()}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" data-testid="text-user-name">
              {userName}
            </p>
            <Badge variant="outline" className="text-xs mt-1">
              {userRole === "sponsor" ? "Sponsor" : "Sponsee"}
            </Badge>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent onClick={handleNavClick}>
        {/* ── Shared navigation ──────────────────────────────────── */}
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {sharedNavItems.map((item) => {
                const active = location === item.url;
                return (
                  <SidebarMenuItem key={item.title}>
                    <NavRow active={active}>
                      <SidebarMenuButton
                        asChild
                        isActive={active}
                        data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <Link href={item.url}>
                          <item.icon className="w-4 h-4" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </NavRow>
                  </SidebarMenuItem>
                );
              })}
              {showFindASponsor && (
                <SidebarMenuItem>
                  <NavRow active={location === "/find-a-sponsor"}>
                    <SidebarMenuButton
                      asChild
                      isActive={location === "/find-a-sponsor"}
                      data-testid="link-find-a-sponsor"
                    >
                      <Link href="/find-a-sponsor">
                        <UserSearch className="w-4 h-4" />
                        <span>Find a Sponsor</span>
                      </Link>
                    </SidebarMenuButton>
                  </NavRow>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* ── Community Portal (all users) ──────────────────────── */}
        <SidebarGroup>
          <SidebarGroupLabel className="h-8 px-2 text-sm text-sidebar-foreground/90 normal-case tracking-normal">
            <Link
              href="/community-portal"
              className={`flex items-center gap-2 w-full hover:text-foreground transition-colors ${
                isCommunityPortalActive ? "text-foreground font-semibold" : "font-medium"
              }`}
              data-testid="link-community-portal"
            >
              <Users2 className="w-4 h-4" />
              <span>Community Portal</span>
            </Link>
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <NavRow active={location === "/messages"}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === "/messages"}
                    data-testid="link-messages"
                  >
                    <Link href="/messages">
                      <MessageCircle className="w-4 h-4" />
                      <span>Messages</span>
                    </Link>
                  </SidebarMenuButton>
                </NavRow>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <NavRow active={isGratitudeListsActive}>
                  <SidebarMenuButton
                    asChild
                    isActive={isGratitudeListsActive}
                    data-testid="link-my-gratitude-lists"
                  >
                    <Link href="/gratitude-lists">
                      <Heart className="w-4 h-4" />
                      <span>Gratitude Lists</span>
                    </Link>
                  </SidebarMenuButton>
                </NavRow>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <NavRow active={location === "/inner-circle"}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === "/inner-circle"}
                    data-testid="link-inner-circle"
                  >
                    <Link href="/inner-circle">
                      <Users2 className="w-4 h-4" />
                      <span>Inner Circle</span>
                    </Link>
                  </SidebarMenuButton>
                </NavRow>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* ── Sponsor Portal group (sponsors only) ───────────────── */}
        {userRole === "sponsor" && (
          <SidebarGroup>
            <SidebarGroupLabel>
              <Link
                href="/sponsor-portal"
                className={`flex items-center gap-1.5 w-full hover:text-foreground transition-colors ${
                  isSponsorPortalActive ? "text-foreground font-semibold" : ""
                }`}
                data-testid="link-sponsor-portal"
              >
                <LayoutDashboard className="w-3.5 h-3.5" />
                <span>Sponsor Portal</span>
              </Link>
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {sponsorPortalItems.map((item) => {
                  const active =
                    location === item.url ||
                    location.startsWith(item.url + "/");
                  return (
                    <SidebarMenuItem key={item.title}>
                      <NavRow active={active}>
                        <SidebarMenuButton
                          asChild
                          isActive={active}
                          data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <Link href={item.url}>
                            <item.icon className="w-4 h-4" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </NavRow>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* ── Support ────────────────────────────────────────────── */}
        <SidebarGroup>
          <SidebarGroupLabel>Support</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <NavRow active={location === "/profile"}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === "/profile"}
                    data-testid="link-profile"
                  >
                    <Link href="/profile">
                      <User className="w-4 h-4" />
                      <span>Profile</span>
                    </Link>
                  </SidebarMenuButton>
                </NavRow>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <NavRow active={location === "/settings"}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === "/settings"}
                    data-testid="link-settings"
                  >
                    <Link href="/settings">
                      <Settings className="w-4 h-4" />
                      <span>Settings</span>
                    </Link>
                  </SidebarMenuButton>
                </NavRow>
              </SidebarMenuItem>
              <AdminSidebarEntry location={location} />
              <SidebarMenuItem>
                <NavRow active={location === "/faq"}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === "/faq"}
                    data-testid="link-faq"
                  >
                    <Link href="/faq">
                      <HelpCircle className="w-4 h-4" />
                      <span>FAQ</span>
                    </Link>
                  </SidebarMenuButton>
                </NavRow>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <NavRow active={location === "/resources"}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === "/resources"}
                    data-testid="link-resources"
                  >
                    <Link href="/resources">
                      <HeartHandshake className="w-4 h-4" />
                      <span>Resources</span>
                    </Link>
                  </SidebarMenuButton>
                </NavRow>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <NavRow active={location === "/contact"}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === "/contact"}
                    data-testid="link-contact"
                  >
                    <Link href="/contact">
                      <LifeBuoy className="w-4 h-4" />
                      <span>Contact Us</span>
                    </Link>
                  </SidebarMenuButton>
                </NavRow>
              </SidebarMenuItem>
              <LogoutSidebarEntry />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
